import {
  generateImageObserverCallback,
  generateImageObserverOptions,
  generateVideoObserverCallback,
  generateVideoObserverOptions,
} from './utils';
import { videoPlayerHandle } from '../PlayPauseButton/utils';
import { buildSectionUrl, getSectionInnerHTML } from '../../sections/PDP/utils';
import AbstractCarousel from '../Carousel';

/**
 * Provides configuration for rendering the PDP zoom gallery modal section.
 *
 * @returns {Object} Section configuration object containing id, section name, and selector.
 */
function getSectionToRender() {
  return {
    id: 'pdp-zoom-gallery-modal',
    section: 'pdp-zoom-gallery',
    selector: '.shopify-section',
  };
}

/**
 * AbstractGallery provides a base implementation for gallery components.
 * Handles carousel navigation, marker events, image/video observers, and keyboard controls.
 *
 * @abstract
 * @class
 * @param {Object} context - Configuration context.
 * @param {HTMLElement} context.gallery - The whole gallery container element.
 * @param {string} context.prefixClassName - Prefix for gallery class names.
 * @param {number} context.gap - Gap between carousel items.
 */
class AbstractGallery extends AbstractCarousel {
  constructor({ gallery, prefixClassName, gap, features }) {
    super({
      gallery,
      prefixClassName: 'carousel-c-scroll',
      gap,
      features,
      extraToggleClass: `${prefixClassName}__marker--active`,
    });
    this.images = gallery.querySelectorAll(`.${prefixClassName}__list-item.${prefixClassName}__list-item--image`);
    this.videos = gallery.querySelectorAll(`.${prefixClassName}__video-container`);
    this.autoplayedVideos = [];
  }

  init() {
    // Sets plenty of kind of events for standard gallery
    throw new Error("Method 'init()' must be implemented");
  }

  bindAbstractGalleryEvents() {
    this.bindAbstractCarouselEvents();
    this.videos.forEach((video) => videoPlayerHandle(video));
  }

  setObservers() {
    // Injects an observer for fetching image media whenever are visible by target
    const imageObserverCallback = generateImageObserverCallback();
    const imageObserverOptions = generateImageObserverOptions(this.carousel);
    this.images.forEach((imageItem) => {
      const observer = new IntersectionObserver(imageObserverCallback, imageObserverOptions);
      observer.observe(imageItem);
    });

    // Injects an observer for fetching video media whenever are visible by target
    const videoObserverCallback = generateVideoObserverCallback(this.autoplayedVideos);
    const videoObserverOptions = generateVideoObserverOptions(this.carousel);
    this.videos.forEach((videoItem) => {
      const observer = new IntersectionObserver(videoObserverCallback, videoObserverOptions);
      observer.observe(videoItem);
    });
  }
}

export class Gallery extends AbstractGallery {
  constructor({ gallery, prefixClassName, features }) {
    super({ gallery, prefixClassName, gap: 4, features });
    this.almostGoneMessage = gallery.querySelector(`.${prefixClassName}__almost-gone`);
    this.anchorImages = gallery.querySelectorAll('a[id^="carousel_"]');
  }

  /**
   * Initialize standard gallery
   */
  init() {
    // Bind all basic events
    this.bindAbstractGalleryEvents();
    // Bind its own events
    this.bindEvents();
  }

  /**
   * Bind its own events
   */
  bindEvents() {
    if (this.features.zoom === false) return;
    this.anchorImages.forEach((anchorImage) =>
      anchorImage.addEventListener('click', this.zoomCarouselHandle.bind(this)),
    );
  }

  zoomCarouselHandle(evt) {
    const sectionToRender = getSectionToRender();
    const url = buildSectionUrl([sectionToRender]);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Zoom Gallery Response error is not okay');
        }
        return response.json();
      })
      .then((data) => {
        const dialogNode = document.querySelector(`#${sectionToRender.id}`);
        const dialogContentNode = dialogNode.querySelector(`#${sectionToRender.id}-content`);
        dialogContentNode.innerHTML = getSectionInnerHTML(data[sectionToRender.section], sectionToRender.selector);
        dialogNode.showModal();
        document.documentElement.style.overflow = 'hidden';

        // Defer this block of code because they aren't user-visible tasks.
        setTimeout(() => {
          // Remove previous keydown event from standard PDP Gallery
          this.disableKeydownEvent();

          // Pause any video already auto-played on standard PDP Gallery
          this.autoplayedVideos.forEach((video) => {
            video.videoElement.pause();
            video.videoPlayer.ariaLabel = 'Play video';
            video.hiddenElement.textContent = 'Play';
          });

          const zoomGallery = dialogContentNode.querySelector('div.pdp-c-zoom-gallery');
          const zoomGalleryInstance = new ZoomGallery({
            gallery: zoomGallery,
            prefixClassName: 'pdp-c-zoom-gallery',
            features: {
              keydown: true,
            },
          });
          zoomGalleryInstance.setObservers();
          zoomGalleryInstance.initPosition(evt);

          const zoomMediaItemClickHandle = () => dialogNode.close();
          zoomGalleryInstance.init(zoomMediaItemClickHandle);

          dialogNode.addEventListener('close', () => {
            // Defer this block of code because they aren't user-visible tasks.
            setTimeout(() => {
              zoomGalleryInstance.close(zoomMediaItemClickHandle);

              document.documentElement.style.overflow = '';
              dialogContentNode.innerHTML = '';
              // Auto play any video already auto-played previously on PDP Gallery
              this.autoplayedVideos.forEach((video) => {
                video.videoElement.play();
                video.videoPlayer.ariaLabel = 'Pause video';
                video.hiddenElement.textContent = 'Pause';
              });
              // Remove 'keydown' event on PDP Zoom Gallery and turn on 'keydown' event on PDP gallery again
              this.enableKeydownEvent();
            }, 0);
          });
        }, 0);
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

class ZoomGallery extends AbstractGallery {
  constructor({ gallery, prefixClassName, features }) {
    super({ gallery, prefixClassName, features });
    this.zoomMediaItems = gallery.querySelectorAll('[id^="zoom_carousel_"]');
  }

  /**
   * Initialize zoom galler and bind a customer handler event
   * @param {Function} handler - it's custom interface
   */
  init(handler) {
    // Bind basic events
    super.bindAbstractGalleryEvents();
    // Bind its own events
    this.bindEvent(handler);
  }

  /**
   * Bind anchor elements with a custom handler
   * @param {Function} handler - it's custom interface
   */
  bindEvent(handler) {
    this.zoomMediaItems.forEach((mediaItem) => mediaItem.addEventListener('click', handler));
  }

  /**
   * An interface that scrolls automatically to specific image clicked from standard gallery container
   * @param {Event} evt - HTML anchor event
   */
  initPosition(evt) {
    // Get position or index from its HTML attributes
    function getPosition(identifier) {
      return Number(identifier.split('_').at(-1));
    }

    // Now you can get the actual widths
    const mediaWrapper = this.carousel.querySelector('img,div');
    const anchorItem = evt.target;
    const index = getPosition(anchorItem.id) - 1;
    // Scrolls it automatically
    this.carousel.scrollTo({
      left: mediaWrapper.offsetWidth * index,
    });

    // Add smooth scrolling behavior then you got specific image on pdp zoom gallery and you clicked from pdp-gallery
    this.carousel.classList.add('pdp-c-zoom-gallery__list-container--smooth');
  }

  /**
   * An interface that closes its own events and abstract events
   * @param {Function} handler - it's custom handler attached to these elements
   */
  close(handler) {
    this.zoomMediaItems.forEach((mediaItem) => mediaItem.removeEventListener('click', handler));
    this.closeAbstractCarouselEvents();
  }
}
